import { useEffect, useRef, useState } from "react";

const EASE_OUT_CUBIC = (progress: number) => 1 - (1 - progress) ** 3;

export function useCountUp(
  target: number,
  options?: { durationMs?: number; enabled?: boolean },
): number {
  const durationMs = options?.durationMs ?? 900;
  const enabled = options?.enabled ?? true;
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!enabled || target <= 0) {
      setValue(target);
      return undefined;
    }

    setValue(0);
    const start = performance.now();

    const tick = (now: number) => {
      const progress = Math.min((now - start) / durationMs, 1);
      const next = Math.round(EASE_OUT_CUBIC(progress) * target);
      setValue((prev) => (prev === next ? prev : next));
      if (progress < 1) {
        requestAnimationFrame(tick);
      }
    };

    const frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, durationMs, enabled]);

  return enabled ? value : target;
}

/**
 * Cost / totals that count on first reveal, then animate between values when inputs change (#11).
 */
export function useLiveCountUp(
  target: number,
  options?: {
    enabled?: boolean;
    /** Delay before the first count (e.g. after a FLIP handoff). */
    startDelayMs?: number;
    /** First landing — MOTION-PRINCIPLES ~1.8s. */
    enterDurationMs?: number;
    /** Slider / plan changes — faster follow. */
    changeDurationMs?: number;
    /** Skip the 0→target enter (return visits). */
    skipEnter?: boolean;
    onEnterComplete?: () => void;
  },
): number {
  const enabled = options?.enabled ?? true;
  const startDelayMs = options?.startDelayMs ?? 0;
  const enterDurationMs = options?.enterDurationMs ?? 1800;
  const changeDurationMs = options?.changeDurationMs ?? 600;
  const skipEnter = options?.skipEnter ?? false;
  const onEnterCompleteRef = useRef(options?.onEnterComplete);
  onEnterCompleteRef.current = options?.onEnterComplete;

  const [displayed, setDisplayed] = useState(() => (!enabled || skipEnter ? target : 0));
  const displayedRef = useRef(displayed);
  displayedRef.current = displayed;
  const hasEnteredRef = useRef(skipEnter);

  useEffect(() => {
    displayedRef.current = displayed;
  }, [displayed]);

  useEffect(() => {
    if (!enabled) {
      setDisplayed(target);
      displayedRef.current = target;
      return undefined;
    }

    if (skipEnter && !hasEnteredRef.current) {
      hasEnteredRef.current = true;
      setDisplayed(target);
      displayedRef.current = target;
      return undefined;
    }

    const from = hasEnteredRef.current ? displayedRef.current : 0;
    if (from === target) {
      return undefined;
    }

    const isEnter = !hasEnteredRef.current;
    const durationMs = isEnter ? enterDurationMs : changeDurationMs;
    hasEnteredRef.current = true;

    let frameId = 0;
    let delayTimer = 0;

    const run = () => {
      const start = performance.now();
      const tick = (now: number) => {
        const progress = Math.min((now - start) / durationMs, 1);
        const next = Math.round(from + (target - from) * EASE_OUT_CUBIC(progress));
        if (next !== displayedRef.current) {
          displayedRef.current = next;
          setDisplayed(next);
        }
        if (progress < 1) {
          frameId = requestAnimationFrame(tick);
        } else if (isEnter) {
          displayedRef.current = target;
          setDisplayed(target);
          onEnterCompleteRef.current?.();
        }
      };
      frameId = requestAnimationFrame(tick);
    };

    if (isEnter && startDelayMs > 0) {
      delayTimer = window.setTimeout(run, startDelayMs);
    } else {
      run();
    }

    return () => {
      window.clearTimeout(delayTimer);
      cancelAnimationFrame(frameId);
    };
  }, [target, enabled, skipEnter, startDelayMs, enterDurationMs, changeDurationMs]);

  return enabled ? displayed : target;
}
